<script setup lang="ts">
import { NxtTabs } from '@future-harry/nxt-styles';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';
import { storeToRefs } from 'pinia';
import type { Language } from '@future-harry/nxt-vue-library';
import { useI18n } from 'vue-i18n';

interface Props {
   errors?: Record<string, string>;
}

const props = withDefaults(defineProps<Props>(), {
   errors: () => ({}),
});

const { t } = useI18n();
const jobBoardStore = useJobBoardStore();
const { currentJobBoard } = storeToRefs(jobBoardStore);

const languages = computed(() => currentJobBoard.value?.languages ?? []);
const activeLanguage = ref<Language | undefined>(languages.value?.[0]);
const activeLanguageName = computed(() => activeLanguage.value?.iso_639_1);

watch(languages, (newLanguages) => {
   if (newLanguages.length > 0 && !activeLanguage.value) {
      activeLanguage.value = newLanguages[0];
   }
});

// if error has language prop as ending (json translatable, `.de`, `.en`, ...)
// example text.en, title.fr, ...
const hasLanguageError = (languageCode: string) => {
   const suffix = `.${languageCode.toLocaleLowerCase()}`;
   const errKeys = Object.keys(props.errors || {});
   return errKeys.some((k) => k.endsWith(suffix));
};
</script>

<template>
   <div class="language-tabs">
      <NxtTabs>
         <p
            v-for="language in languages"
            :key="language.id"
            :class="['language-tab', { '-active': language.id === activeLanguage?.id, '-error': hasLanguageError(language.iso_639_1) }]"
            @click="activeLanguage = language"
         >
            {{ language.iso_639_1.toUpperCase() }}
         </p>
      </NxtTabs>

      <slot :active-language="activeLanguage" :active-language-name="activeLanguageName" :languages="languages" />
   </div>
</template>

<style scoped lang="scss">
.language-tabs {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
}

:deep(.nxt-tabs) {
   display: flex;
   gap: 0px;
   border-bottom: 1px solid var(--nxt-light-grey);
}

.language-tab {
   cursor: pointer;
   padding: 12px 16px;
   border: 1px solid var(--nxt-light-grey);
   border-bottom: 2px solid var(--nxt-light-grey);
   margin: -1px -1px 0 -1px;
   transition: all 0.2s ease;
   background-color: var(--nxt-extralight-grey);

   &:first-child {
      border-top-left-radius: 10px;
      margin-left: 0;
      border-left: 1px solid var(--nxt-light-grey);
   }

   &:last-child {
      border-top-right-radius: 10px;
   }

   &.-active {
      background-color: var(--nxt-white);
      color: var(--nxt-main);
      font-weight: bold;
      border-color: var(--nxt-main);
      border-bottom-color: var(--nxt-main);
      z-index: 1;
   }

   &.-error {
      position: relative;

      &::after {
         content: '!';
         position: absolute;
         top: 2px;
         right: 6px;
         color: var(--nxt-red);
         font-weight: bold;
         font-size: 12px;
      }
   }
}
</style>
